// Cross-component signal for "this loan's submission (conditions / documents) just changed".
//
// Condition edits happen on several screens (Conditions page, condition modals, task modals,
// exception responses) while the floater tabs keep their own copy of the submission. Without a
// signal the Notify Borrower ("notification") tab kept showing the outstanding conditions it read
// when it was first opened, so the page had to be reloaded to see an edit. Mutating services emit
// here; consumers refetch.

type Listener = (loanNumber: number) => void

const listeners = new Set<Listener>()

/** Emitted by the submission mutation services. `loanNumber` 0 means "the loan in context". */
export const notifyLoanSubmissionChanged = (loanNumber: number = 0) => {
  listeners.forEach((listener) => {
    try {
      listener(loanNumber)
    } catch (error) {
      console.error('loanSubmissionSync listener failed:', error)
    }
  })
}

/** Subscribe to submission changes. Returns the unsubscribe function. */
export const onLoanSubmissionChanged = (listener: Listener) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// The Documents floater and the Conditions page each hold their own copy of the loan's documents.
// A document added in the floater was missing from the Conditions page's copy, so dropping it onto
// a condition found nothing to assign until a reload. The floater emits the document payload it
// just saved (same `set` / `update` / `delete` shape as updateLoanSubmission) so the page can apply it.

export type LoanDocumentsChange = Record<string, Array<Record<string, any>>>

type DocumentsListener = (loanNumber: number, change: LoanDocumentsChange) => void

const documentsListeners = new Set<DocumentsListener>()

export const notifyLoanDocumentsChanged = (loanNumber: number, change: LoanDocumentsChange) => {
  documentsListeners.forEach((listener) => {
    try {
      listener(loanNumber, change)
    } catch (error) {
      console.error('loanSubmissionSync documents listener failed:', error)
    }
  })
}

export const onLoanDocumentsChanged = (listener: DocumentsListener) => {
  documentsListeners.add(listener)
  return () => {
    documentsListeners.delete(listener)
  }
}

/** Mirrors how the backend applies a documents payload: set replaces, update upserts by key, delete removes by key. */
export const applyLoanDocumentsChange = <T extends { key: string }>(docs: T[], change: LoanDocumentsChange): T[] => {
  if (change.set) return change.set as T[]
  let next = docs
  if (change.update?.length) {
    next = [...next]
    change.update.forEach((updated) => {
      if (!updated?.key) return
      const index = next.findIndex((doc) => doc.key == updated.key)
      if (index == -1) next.push(updated as T)
      else next[index] = { ...next[index], ...updated }
    })
  } else if (change.delete?.length) {
    const deletedKeys = new Set(change.delete.map((doc) => doc.key))
    next = next.filter((doc) => !deletedKeys.has(doc.key))
  }
  return next
}
